import { useState } from 'react';

import { IconJourneyItem } from '@components/icons/journey-icon';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { useManageJourneys, useSetJourneyEnabled } from './hooks';
import { useJourneyConfigSidebar } from './journey-config-sidebar';

import { JourneyListSection } from '../components/journey-list-section';
import { JOURNEYS_LIST_CAP } from '../components/journey-search';
import { journeyListStrings } from '../components/list-strings';
import { SectionBox } from '../components/section-box';
import { JourneyListRow } from '../components/types';

export const ManageTab = () => {
  const { t } = useScopedTranslate(locale);
  const [search, setSearch] = useState('');
  const journeysQuery = useManageJourneys(search);
  const setEnabled = useSetJourneyEnabled();
  const openConfigSidebar = useJourneyConfigSidebar();

  const handleToggle = (journey: JourneyListRow, isEnabled: boolean) => {
    setEnabled.mutate({ journeyId: journey.journeyId, isEnabled, journeyTitle: journey.title });
  };

  const handleConfigure = (journey: JourneyListRow) => {
    openConfigSidebar({
      journeyId: journey.journeyId,
      journeyTitle: journey.title,
      journeyDescription: journey.description,
    });
  };

  return (
    <SectionBox>
      <Stack space={5}>
        <Stack space={3} orientation="horizontal" align="center">
          <IconJourneyItem size={24} color="indigo200" />
          <Text font="bold16" text={t('sectionTitle')} />
        </Stack>
        <Text font="regular14" color="textSecondary" text={t('sectionDescription')} />

        <JourneyListSection
          journeys={journeysQuery.data?.results}
          isLoading={journeysQuery.isLoading}
          isError={journeysQuery.isError}
          isTruncated={journeysQuery.data?.isTruncated}
          searchValue={search}
          onSearchChange={setSearch}
          onToggle={handleToggle}
          onConfigure={handleConfigure}
          onRetry={() => journeysQuery.refetch()}
          mutatingJourneyId={setEnabled.isLoading ? setEnabled.variables?.journeyId : null}
          journeyRowCy="journey-row"
          toggleCy="journey-toggle"
          searchTestid="journeys-manage-search"
          loadingCy="manage-journeys-loading"
          errorCy="manage-journeys-error"
          labels={{
            all: t('all'),
            enabled: t('enabled'),
            disabled: t('disabled'),
            searchPlaceholder: t('searchPlaceholder'),
            noJourneys: t('noJourneys'),
            noResults: t('noResults'),
            errorDescription: t('loadError'),
            retry: t('retry'),
            configure: t('configure'),
            toggleEnabledLabel: t('enabled'),
            toggleDisabledLabel: t('disabled'),
            showingFirstResults: t('showingFirstResults', { count: String(JOURNEYS_LIST_CAP) }),
          }}
        />
      </Stack>
    </SectionBox>
  );
};

const locale = {
  en: {
    ...journeyListStrings.en,
    sectionTitle: 'Journeys available in Hire',
    sectionDescription:
      'Choose which journeys hiring managers can send applicants to, and configure which fields they fill in for each one. Fewer journeys and fewer fields = faster, less confusing for your team',
    all: 'All',
    enabled: 'Enabled',
    disabled: 'Disabled',
    searchPlaceholder: 'Search...',
    noJourneys: 'No journeys found',
    retry: 'Retry',
    configure: 'Configure',
  },
  is: {
    ...journeyListStrings.is,
    sectionTitle: 'Journey-ferli í boði í Hire',
    sectionDescription:
      'Veldu hvaða Journey-ferli ráðningarstjórar geta sent umsækjendur í og hvaða reiti þeir fylla út fyrir hvert ferli. Færri ferli og reitir gera vinnuna fljótlegri og einfaldari fyrir teymið.',
    all: 'Öll',
    enabled: 'Virk',
    disabled: 'Óvirk',
    searchPlaceholder: 'Leita…',
    noJourneys: 'Engin Journey-ferli fundust',
    retry: 'Reyna aftur',
    configure: 'Stilla',
  },
};
